import React from "react";
import {
  Box,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import Typography from "../i18n/LocalizedTypography";
import {
  isOuterPlanetKey,
  useUserPreferences,
} from "../../contexts/UserPreferencesContext";
import {
  transitPlanetDetails,
  transitTimezoneLabel,
} from "../../utils/transitOverlay";
import { useCurrentTransitOverlay } from "../../hooks/useCurrentTransitOverlay";

type Props = {
  enabled: boolean;
  overlay: ReturnType<typeof useCurrentTransitOverlay>;
};
export default function TransitOverlayControls({ enabled, overlay }: Props) {
  if (!enabled || !overlay?.transitTiming) return null;
  const { timing, setTiming, step, reset, timezoneOffset } =
    overlay.transitTiming;
  return (
    <Box sx={{ width: "100%", my: 1.5, minWidth: 0 }}>
      <Stack spacing={1}>
        <Typography variant="caption">
          Transit date and time · Birthplace time (
          {transitTimezoneLabel(timezoneOffset)})
        </Typography>
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          <Button
            variant="outlined"
            size="small"
            aria-label="Previous month"
            onClick={() => step("month", -1)}
          >
            − Month
          </Button>
          <Button
            variant="outlined"
            size="small"
            aria-label="Previous day"
            onClick={() => step("day", -1)}
          >
            − Day
          </Button>
          <Button
            variant="outlined"
            size="small"
            aria-label="Next day"
            onClick={() => step("day", 1)}
          >
            + Day
          </Button>
          <Button
            variant="outlined"
            size="small"
            aria-label="Next month"
            onClick={() => step("month", 1)}
          >
            + Month
          </Button>
          <Button size="small" onClick={reset}>
            Now
          </Button>
        </Stack>
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          <TextField
            type="date"
            size="small"
            label="Transit date"
            value={timing.date}
            onChange={(event) =>
              setTiming({ ...timing, date: event.target.value })
            }
            slotProps={{ inputLabel: { shrink: true } }}
            sx={{ minWidth: 160, flex: 1 }}
          />
          <TextField
            type="time"
            size="small"
            label="Transit time"
            value={timing.time}
            onChange={(event) =>
              setTiming({ ...timing, time: event.target.value })
            }
            slotProps={{ inputLabel: { shrink: true }, htmlInput: { step: 1 } }}
            sx={{ minWidth: 140, flex: 1 }}
          />
        </Stack>
        {overlay.loading ? (
          <Typography variant="caption">
            Calculating selected transit…
          </Typography>
        ) : null}
        {overlay.error ? (
          <Typography variant="caption" color="error">
            {overlay.error}
          </Typography>
        ) : null}
      </Stack>
    </Box>
  );
}

export function TransitOverlayTable({ enabled, overlay }: Props) {
  const { t } = useTranslation();
  const { showOuterPlanets } = useUserPreferences();
  if (!enabled || !overlay?.transitTiming) return null;
  const planets = Object.entries(overlay.transitChart?.planets || {}).filter(
    ([key, planet]: [string, any]) =>
      showOuterPlanets || !isOuterPlanetKey(planet?.name || key),
  );
  return (
    <Box sx={{ width: "100%", minWidth: 0, my: 1.5 }}>
      {planets.length > 0 && !overlay.loading && !overlay.error ? (
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small" aria-label="Selected transit planet details">
            <TableHead>
              <TableRow>
                <TableCell>Transit planet</TableCell>
                <TableCell>Sign</TableCell>
                <TableCell>Degree</TableCell>
                <TableCell>Attributes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {planets.map(([key, planet]) => {
                const info = transitPlanetDetails(
                  overlay.transitChart,
                  key,
                  planet,
                );
                return (
                  <TableRow key={key}>
                    <TableCell>
                      {t(`astrology.planets.${info.name}`, {
                        defaultValue: info.name,
                      })}
                    </TableCell>
                    <TableCell>
                      {t(`astrology.signs.${info.sign}`, {
                        defaultValue: String(
                          (planet as any)?.sign?.name || "—",
                        ),
                      })}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {info.formattedDegree}
                    </TableCell>
                    <TableCell>{info.attributes.join(", ") || "—"}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      ) : null}
      <Typography variant="caption">
        T = Transit · E = Exalted · D = Debilitated · R = Retrograde · C =
        Combust
      </Typography>
    </Box>
  );
}
